// Golem fork (FT-1487): THE TOWN'S GEOMETRY — where the seat ring sits and
// where the central cross area lands, dialled per backdrop.
//
// L.O.T.S. dropped the clocktower's centre disc (the clock face). The town
// square is now a RING of portrait tarot cards (FT-1469) around a CENTRAL
// CROSS AREA — five reserved slots laid over the backdrop's own painted
// panels (the Cart's five black frames are the first target). The middle slot
// carries the player counts and, later, the end-game winner disc; the other
// four are reserved for a later slice to dress.
//
// Built as the readerColor / townBg / coinArt twin: the choice is published as
// CSS custom properties on the document root, so a change is a repaint —
// TownSquare's ring geometry reads var(--seat-ring-scale), and TownCross reads
// the --cross-* vars. A LOCAL choice, this browser
// only, remembered PER BACKDROP: each backdrop wears a different painting, so
// the panels sit in different places and the ring wants a different radius.
// The stored value is a map keyed by backdrop id; golem/prefs drives which
// backdrop is live (the `townBg` pref) and calls applyTownLayout on every
// prefs landing, exactly as it calls applyTownBg.
import Vue from "vue";

const KEY = "golem.townLayout"; // { [bgId]: { ...dials } }

// The dials, with their shipped defaults and bounds. One entry per knob the
// lab offers. `var` is the CSS custom property each writes. Defaults are the
// "circle, panels on the Cart" starting point.
export const DIALS = [
  // The seat ring's radius, as a multiplier on its natural 50%-of-height
  // radius. 1 is the app's standing ring; above 1 pushes the seats outward so
  // they clear the central cross.
  {
    key: "ringScale",
    label: "Ring radius",
    var: "--seat-ring-scale",
    min: 0.6,
    max: 1.6,
    step: 0.01,
    def: 1.04,
  },
  // NOTE (FT-1487): a separate x/y radius (oval) is deferred — the ring places
  // seats by pure rotation off one spoke length, so a true ellipse needs each
  // seat placed by computed x/y coordinates (a JS rewrite of the ring), not a
  // container scale, which shears the rotated tarot cards. Left for a follow-up.
  // The whole cross's offset from the viewport centre, in vh. The Cart's
  // middle panel sits a hair right of and level with centre.
  {
    key: "crossX",
    label: "Cross X",
    var: "--cross-x",
    min: -20,
    max: 20,
    step: 0.5,
    def: 0.7,
  },
  {
    key: "crossY",
    label: "Cross Y",
    var: "--cross-y",
    min: -20,
    max: 20,
    step: 0.5,
    def: -4,
  },
  // The reserved slots' size, as a multiplier on the measured panel size.
  {
    key: "slotScale",
    label: "Slot size",
    var: "--cross-slot-scale",
    min: 0.5,
    max: 1.8,
    step: 0.01,
    def: 1.0,
  },
  // How far the four outer slots spread from the middle one, as a multiplier
  // on the measured panel offsets.
  {
    key: "spread",
    label: "Spread",
    var: "--cross-spread",
    min: 0.5,
    max: 1.6,
    step: 0.01,
    def: 1.0,
  },
];

const DEFAULTS = DIALS.reduce((o, d) => ((o[d.key] = d.def), o), {});

function readStore() {
  try {
    const raw = localStorage.getItem(KEY);
    const map = raw ? JSON.parse(raw) : null;
    return map && typeof map === "object" ? map : {};
  } catch (e) {
    return {};
  }
}

function writeStore(map) {
  try {
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch (e) {
    // storage off — the in-memory state below still drives this session
  }
}

// One dial set for a backdrop: its stored values folded over the defaults, so
// a new dial ships with a default even for a backdrop saved before it existed.
function dialsFor(bgId, map) {
  const saved = (map && map[bgId]) || {};
  const out = {};
  for (const d of DIALS) {
    const v = Number(saved[d.key]);
    out[d.key] = Number.isFinite(v) ? v : d.def;
  }
  return out;
}

// The live geometry: which backdrop is showing, and that backdrop's dials.
// The lab binds straight to `dials`; TownCross/TownSquare read the painted
// vars, not this — this is only the lab's model and the persistence.
export const townLayoutState = Vue.observable({
  bg: "town",
  dials: { ...DEFAULTS },
  // Dev-only, not persisted: draw the four reserved slots' outlines so the
  // lab can line them up with the backdrop's painted panels.
  outline: false,
});

/** Paint one backdrop's dials onto the root so the ring and cross follow. */
function paint(dials) {
  const root = document.documentElement;
  root.style.setProperty("--seat-ring-scale", String(dials.ringScale));
  root.style.setProperty("--cross-x", String(dials.crossX));
  root.style.setProperty("--cross-y", String(dials.crossY));
  root.style.setProperty("--cross-slot-scale", String(dials.slotScale));
  root.style.setProperty("--cross-spread", String(dials.spread));
}

/**
 * The town backdrop just landed (golem/prefs, or the picker): load THAT
 * backdrop's remembered geometry and paint it. Called with the backdrop id
 * the `townBg` pref holds.
 */
export function applyTownLayout(bgId) {
  const id = bgId || "town";
  const dials = dialsFor(id, readStore());
  townLayoutState.bg = id;
  townLayoutState.dials = dials;
  paint(dials);
}

/** One dial moved in the lab: update the live backdrop's set, repaint, save. */
export function setDial(key, value) {
  if (!DIALS.some((d) => d.key === key)) return;
  const v = Number(value);
  if (!Number.isFinite(v)) return;
  townLayoutState.dials = { ...townLayoutState.dials, [key]: v };
  paint(townLayoutState.dials);
  const map = readStore();
  map[townLayoutState.bg] = { ...townLayoutState.dials };
  writeStore(map);
}

/** Dev toggle: show/hide the reserved slots' outlines (session-only). */
export function setOutline(on) {
  townLayoutState.outline = !!on;
}

/** Put the live backdrop back to the shipped defaults. */
export function resetLayout() {
  townLayoutState.dials = { ...DEFAULTS };
  paint(townLayoutState.dials);
  const map = readStore();
  map[townLayoutState.bg] = { ...townLayoutState.dials };
  writeStore(map);
}

// Paint the standing backdrop's geometry at import, before prefs lands — so
// the first frame already carries a ring scale and cross vars (the CSS-side
// fallbacks cover anything this misses).
applyTownLayout(townLayoutState.bg);
